import React from 'react';
import './TermsOfService.css';

const EFFECTIVE_DATE = 'May 6, 2025';

function TermsOfService({ onBack }) {
  return (
    <div className="Legal-page">
      <div className="Legal-nav">
        <button className="Legal-back" onClick={onBack}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          Back
        </button>
        <span className="Legal-nav-brand">PlutoBible</span>
      </div>

      <div className="Legal-content">
        <h1>Terms of Service</h1>
        <p className="Legal-meta">Effective date: {EFFECTIVE_DATE}</p>

        <p>
          These Terms of Service ("Terms") govern your access to and use of PlutoBible, including
          its website, mobile web application, and any related services (collectively, the "Service"),
          operated by <strong>BytePluto Inc.</strong>, a corporation incorporated under the laws of the
          Province of Ontario, Canada, with its principal place of business in Toronto, Ontario
          ("BytePluto", "we", "us", or "our").
        </p>
        <p>
          By accessing or using the Service, you agree to be bound by these Terms. If you do not
          agree, please do not use the Service.
        </p>

        <h2>1. Description of the Service</h2>
        <p>
          PlutoBible is a Bible study application that provides access to multiple Bible translations,
          personal study tools (notes, bookmarks, highlights), AI-powered chat assistance, cross-references,
          and commentary content. The Service is intended for personal, non-commercial spiritual study and
          educational use.
        </p>

        <h2>2. Eligibility</h2>
        <p>
          You must be at least 13 years of age to use the Service. If you are under the age of majority
          in your jurisdiction, you represent that a parent or legal guardian has reviewed and agreed to
          these Terms on your behalf. By using the Service, you represent that you meet these requirements.
        </p>

        <h2>3. User Accounts</h2>
        <p>
          Certain features of the Service require you to create an account. You agree to provide accurate,
          current, and complete information during registration and to keep your account credentials
          confidential. You are responsible for all activity that occurs under your account. Notify us
          immediately at <a href="mailto:apps-support@bytepluto.org">apps-support@bytepluto.org</a> if
          you suspect unauthorized access to your account.
        </p>
        <p>
          You may use the Service without an account for basic Bible reading. Features such as notes,
          bookmarks, and highlights require a registered account and are synced to our servers.
        </p>

        <h2>4. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Service for any unlawful purpose or in violation of any applicable laws or regulations;</li>
          <li>Attempt to gain unauthorized access to any part of the Service, our servers, or any connected systems;</li>
          <li>Interfere with or disrupt the integrity or performance of the Service;</li>
          <li>Scrape, crawl, or systematically extract content from the Service without our express written permission;</li>
          <li>Use automated means to create accounts, submit content, or interact with the Service;</li>
          <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity;</li>
          <li>Post or transmit any content that is unlawful, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable.</li>
        </ul>

        <h2>5. User Content</h2>
        <p>
          The Service allows you to create and store personal content such as study notes, bookmarks,
          and highlights ("User Content"). You retain full ownership of your User Content. By submitting
          User Content to the Service, you grant BytePluto a limited, non-exclusive, royalty-free licence
          to store and transmit your User Content solely for the purpose of providing the Service to you.
        </p>
        <p>
          We do not use your personal notes or study content for advertising, model training, or any
          purpose other than delivering the Service to you.
        </p>

        <h2>6. AI Features</h2>
        <p>
          PlutoBible offers AI-assisted Bible study tools, including an in-app chat assistant. AI responses
          are generated for informational and devotional purposes only and do not constitute theological
          advice, counselling, or authoritative doctrinal teaching. AI responses may contain errors or
          inaccuracies. You are encouraged to verify any scriptural references against your chosen Bible
          translation. BytePluto is not responsible for decisions made based on AI-generated content.
        </p>
        <p>
          Depending on your device and configuration, AI features may be processed locally on your device
          (using on-device models) or through third-party AI service providers. No personal identifying
          information is sent to AI providers as part of your queries.
        </p>

        <h2>7. Payments and Voluntary Support</h2>
        <p>
          PlutoBible is currently available free of charge. We accept voluntary financial contributions
          ("Support") to help sustain and improve the Service. All Support payments are processed securely
          by <strong>Stripe</strong>, a third-party payment processor. By making a Support payment, you
          also agree to Stripe's Terms of Service and Privacy Policy.
        </p>
        <p>
          <strong>Voluntary contributions are non-refundable</strong> except where required by applicable
          law. Contributions do not entitle you to any specific features, deliverables, or service level
          beyond what is generally available to all users.
        </p>

        <h2>8. Subscriptions (Future)</h2>
        <p>
          BytePluto may introduce paid subscription plans in the future to unlock premium features. When
          subscription plans become available, the applicable pricing, billing terms, and cancellation
          policy will be disclosed clearly before any charge is made. You will not be enrolled in any
          subscription without your explicit consent. These Terms will be updated to reflect subscription
          terms at that time.
        </p>

        <h2>9. Intellectual Property</h2>
        <p>
          The Service, including its design, code, branding, logos, and non-Bible original content, is
          owned by BytePluto Inc. and is protected by Canadian and international intellectual property laws.
          You may not copy, modify, distribute, or create derivative works of the Service or its content
          without our prior written permission.
        </p>
        <p>
          Bible texts displayed in the Service are sourced from public domain translations or translations
          licensed for free distribution. Commentary texts (Matthew Henry, Adam Clarke, Jamieson-Fausset-Brown,
          John Gill) are sourced from public domain works written in the 1600s–1800s. These texts remain
          in the public domain.
        </p>

        <h2>10. Third-Party Services</h2>
        <p>
          The Service integrates with third-party services including Google Firebase (authentication and
          storage), Stripe (payment processing), and others. Your use of these integrated services is
          subject to their respective terms and privacy policies. BytePluto is not responsible for the
          practices or content of any third-party services.
        </p>

        <h2>11. Availability and Modifications</h2>
        <p>
          We strive to keep the Service available but do not guarantee uninterrupted access. We reserve
          the right to modify, suspend, or discontinue any part of the Service at any time, with or without
          notice. We will make reasonable efforts to notify users of significant changes.
        </p>

        <h2>12. Disclaimers</h2>
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
          IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, AND NON-INFRINGEMENT. BYTEPLUTO DOES NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE,
          SECURE, OR CONTINUOUSLY AVAILABLE.
        </p>
        <p>
          PlutoBible is a Bible study tool and is not affiliated with any church, denomination, or
          theological institution. Content on the Service does not represent the official position of any
          religious body.
        </p>

        <h2>13. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, BYTEPLUTO INC. AND ITS OFFICERS, DIRECTORS,
          EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
          OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE SERVICE,
          EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p>
          IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO
          US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED CANADIAN DOLLARS (CAD $100).
        </p>

        <h2>14. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless BytePluto Inc. and its officers, directors, employees,
          and agents from and against any claims, damages, losses, liabilities, and expenses (including
          reasonable legal fees) arising out of or related to your use of the Service, your User Content,
          or your violation of these Terms.
        </p>

        <h2>15. Governing Law and Dispute Resolution</h2>
        <p>
          These Terms are governed by and construed in accordance with the laws of the Province of Ontario
          and the federal laws of Canada applicable therein, without regard to conflict of law principles.
          Any dispute arising under these Terms shall be subject to the exclusive jurisdiction of the courts
          located in Toronto, Ontario, Canada, and you consent to personal jurisdiction in such courts.
        </p>

        <h2>16. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. When we do, we will update the effective date at
          the top of this page. For material changes, we will make reasonable efforts to notify you
          (for example, by displaying a notice in the app or sending an email to your registered address).
          Your continued use of the Service after any update constitutes acceptance of the revised Terms.
        </p>

        <h2>17. Contact Us</h2>
        <p>
          If you have any questions about these Terms, please contact us at:
        </p>
        <address>
          <strong>BytePluto Inc.</strong><br />
          Toronto, Ontario, Canada<br />
          <a href="mailto:apps-support@bytepluto.org">apps-support@bytepluto.org</a>
        </address>
      </div>

      <footer className="Legal-footer">
        <p>&copy; {new Date().getFullYear()} BytePluto Inc. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default TermsOfService;
